import { useState, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';

export interface FilterSectionProps {
  label: string;
  /**
   * Cuántos valores tiene puestos esta faceta. Sale en la cabecera para que se
   * lea sin abrirla, y decide si la sección arranca abierta.
   */
  count?: number;
  /** Lo puesto, dicho corto, cuando no es un número: «Últimos 30 días». */
  summary?: string | undefined;
  children: ReactNode;
  className?: string;
}

/**
 * Una faceta adentro del panel de filtros: se abre EN EL SITIO.
 *
 * Es la alternativa al desplegable para cuando la faceta ya vive adentro de un
 * panel. Abrir ahí un popover encima de otro funciona —Radix apila las capas
 * bien— pero la lista de opciones cae tapando a las otras siete facetas, que es
 * exactamente lo que el panel venía a resolver: poder poner varias sin andar
 * reabriendo nada. Acá la lista empuja hacia abajo y el panel scrollea.
 *
 * Arranca abierta si la faceta ya está filtrando, así que un enlace que llega
 * con filtros puestos los muestra al abrir el panel en vez de esconderlos
 * detrás de ocho cabeceras cerradas.
 */
export function FilterSection({ label, count = 0, summary, children, className }: FilterSectionProps) {
  const [open, setOpen] = useState(count > 0);

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className={cn('rounded-md border border-border', count > 0 && 'border-solid', className)}
    >
      <CollapsibleTrigger className="flex w-full items-center gap-1.5 rounded-md px-2 py-1.5 text-left text-sm transition-colors duration-150 ease-out hover:bg-muted">
        <span className="truncate font-medium">{label}</span>
        {count > 0 && !summary && (
          <Badge variant="secondary" className="tabular">
            {count}
          </Badge>
        )}
        {summary && (
          <span className="min-w-0 truncate text-xs text-muted-foreground">{summary}</span>
        )}
        <ChevronDown
          className={cn(
            'ml-auto size-3.5 shrink-0 text-muted-foreground transition-transform duration-150 ease-out',
            open && 'rotate-180',
          )}
          aria-hidden
        />
      </CollapsibleTrigger>

      <CollapsibleContent className="border-t border-border">{children}</CollapsibleContent>
    </Collapsible>
  );
}
